import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import RewardController from '@/controller/reward';

const useRewards = () => {
    const [rewards, setRewards] = useState([]);
    const [history, setHistory] = useState([]);
    const [options, setOptions] = useState({ categories: [], brands: [], point_ranges: [] });
    const [balance, setBalance] = useState({ current: 0, lifetime: 0, tier: null });

    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 12,
        category_id: null,
        points_range: null,
        brand_id: null,
        search: '',
    });

    const fetchPointBalance = useCallback(async (signal) => {
        try {
            const res = await RewardController.getPointBalance(signal);
            if (res.status === 200 && res.data) {
                setBalance({
                    current: res.data.balance || 0,
                    lifetime: res.data.lifetime_balance || 0,
                    tier: res.data.tier,
                });
            }
        } catch (error) {
            console.error('Failed to fetch point balance', error);
        }
    }, []);

    const fetchRewards = useCallback(
        async (signal) => {
            setLoading(true);
            try {
                const res = await RewardController.getRewards(filters, signal);
                if (res.status === 200 && res.data) {
                    setRewards(res.data.data);
                    setTotal(res.data.meta?.total || 0);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    toast.error('Failed to fetch rewards.');
                }
            } finally {
                setLoading(false);
            }
        },
        [filters],
    );

    const fetchHistory = useCallback(async (signal) => {
        setHistoryLoading(true);
        try {
            const res = await RewardController.getRedemptionHistory({ page: 1, limit: 10 }, signal);
            if (res.status === 200 && res.data) {
                setHistory(res.data.data);
                setHistoryTotal(res.data.meta?.total || 0);
            }
        } catch (error) {
            console.error('Failed to fetch redemption history', error);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const fetchOptions = useCallback(async (signal) => {
        try {
            const res = await RewardController.getRewardOptions(signal);
            if (res.status === 200 && res.data) {
                setOptions(res.data);
            }
        } catch (error) {
            console.error('Failed to fetch filter options', error);
        }
    }, []);

    const redeem = async (rewardId) => {
        try {
            const res = await RewardController.redeemReward(rewardId);
            if (res.status === 200) {
                toast.success(res.data?.__message || 'Reward claimed successfully!');
                // Refresh balance, history, and rewards list
                fetchPointBalance();
                fetchHistory();
                fetchRewards();
                return { success: true, data: res.data.data };
            } else {
                toast.error(res.data?.__message || 'Failed to claim reward.');
                return { success: false, error: res.data?.__message };
            }
        } catch (error) {
            toast.error('An error occurred while claiming reward.');
            return { success: false, error: error.message };
        }
    };

    useEffect(() => {
        const ctrl = new AbortController();
        fetchPointBalance(ctrl.signal);
        fetchOptions(ctrl.signal);
        return () => ctrl.abort();
    }, [fetchPointBalance, fetchOptions]);

    useEffect(() => {
        const ctrl = new AbortController();
        fetchRewards(ctrl.signal);
        return () => ctrl.abort();
    }, [fetchRewards]);

    return {
        rewards,
        history,
        options,
        balance,
        loading,
        historyLoading,
        total,
        historyTotal,
        filters,
        setFilters,
        redeem,
        refresh: () => {
            fetchRewards();
            fetchPointBalance();
        },
        fetchHistory,
    };
};

export default useRewards;
